import AddIcon from "@mui/icons-material/AddCircle";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Unstable_Grid2";
import { useSelector } from "react-redux";
import { TransitionGroup } from "react-transition-group";
import { v4 } from "uuid";
import SRC_TYPES from "../../dictionaries/srcTypes.json";
import { buildSegment } from "../../redux/sales";
import { selectAirlineByTktNumber } from "../../redux/airlines";
import EditItinerarySegment from "./EditItinerarySegment";
import EditSaleForm from "./EditSaleForm";

const emptyItinerary = [];

const isTktNumber = (tkt) => /^\d{0,13}$/.test(tkt);

const EditAirSaleForm = (props) => {
  const { isRefund, onChange, sale, title } = props;

  const isExchange = sale?.type === "TX";
  const isEmd = ["ES", "ER", "EX"].includes(sale?.type);

  const itinerary = sale?.itinerary || emptyItinerary;
  const pax = sale?.pax || "";
  const pnr = sale?.pnr || "";
  const relTkt = sale?.relTkt || "";
  const src = sale?.src;
  const tkt = sale?.tkt || "";

  const airline = useSelector((state) =>
    selectAirlineByTktNumber(state, tkt.slice(0, -10)),
  );

  const airlineLabel = airline
    ? `(${airline.iata}) ${airline?.name}`
    : tkt.length < 13
      ? "Enter Tkt Number"
      : "Unknown Airline";

  const onSaleChange = (changes) => {
    onChange({ ...sale, ...changes });
  };

  const onPassengerChange = (event) => {
    const pax = event.target.value;
    onSaleChange({ pax });
  };

  const onPnrChange = (event) => {
    const value = event.target.value;
    if (value === "" || /^[0-9A-Za-z]{0,8}$/u.test(value)) {
      const pnr = value.toUpperCase();
      onSaleChange({ pnr });
    }
  };

  const onRelTktNumberChange = (event) => {
    const relTkt = event.target.value;
    if (isTktNumber(relTkt)) {
      onSaleChange({ relTkt });
    }
  };

  const onSegmentAdd = () => {
    const changedItinerary = [...itinerary, { ...buildSegment(), id: v4() }];
    onSaleChange({ itinerary: changedItinerary });
  };

  const onSegmentChange = (updatedSegment) => {
    const changedItinerary = itinerary.map((segment) =>
      updatedSegment.id === segment.id ? updatedSegment : segment,
    );
    onSaleChange({ itinerary: changedItinerary });
  };

  const onSegmentRemove = (segmentId) => {
    const changedItinerary = itinerary.filter(({ id }) => id !== segmentId);
    onSaleChange({ itinerary: changedItinerary });
  };

  const onTktNumberChange = (event) => {
    const tkt = event.target.value;
    if (isTktNumber(tkt)) {
      onSaleChange({ tkt });
    }
  };

  return (
    <EditSaleForm
      isRefund={isRefund}
      onChange={onSaleChange}
      sale={sale}
      subTitle={title}
    >
      <Grid xs={12}>
        <TextField
          autoFocus
          fullWidth
          label="Passenger"
          onChange={onPassengerChange}
          required
          value={pax}
        />
      </Grid>
      <Grid xs={src ? 6 : 12}>
        <TextField fullWidth label="PNR" onChange={onPnrChange} value={pnr} />
      </Grid>
      {src && (
        <Grid xs={6}>
          <TextField
            disabled
            fullWidth
            label="Source"
            value={SRC_TYPES[src]?.name}
          />
        </Grid>
      )}
      <Grid xs={12}>
        <TextField
          fullWidth
          label="Tkt Number"
          onChange={onTktNumberChange}
          placeholder="Enter 13 digits"
          required
          value={tkt}
        />
      </Grid>
      {(isExchange || isEmd) && (
        <Grid xs={12}>
          <TextField
            fullWidth
            label={isEmd ? "Related Ticket" : "Previous Tkt Number"}
            onChange={onRelTktNumberChange}
            placeholder="Enter 13 digits"
            required
            value={relTkt}
          />
        </Grid>
      )}
      <Grid xs={12}>
        <TextField
          disabled
          fullWidth
          InputProps={{ readOnly: true }}
          label="Airline"
          value={airlineLabel}
        />
      </Grid>
      <Grid xs={12}>
        <Divider textAlign="left">Itinerary</Divider>
      </Grid>
      <Grid xs={12}>
        <TransitionGroup component={Grid} container spacing={1}>
          {itinerary.map((segment, index) => (
            <Collapse key={segment.id} component={null}>
              {index > 0 && (
                <Grid xs={12}>
                  <Divider />
                </Grid>
              )}
              <EditItinerarySegment
                segment={segment}
                onChange={onSegmentChange}
                onRemove={onSegmentRemove}
              />
            </Collapse>
          ))}
        </TransitionGroup>
        <Grid textAlign="center" xs={12}>
          <FormControlLabel
            control={
              <IconButton onClick={onSegmentAdd} disableRipple>
                <AddIcon />
              </IconButton>
            }
            label="Add Segment"
          />
        </Grid>
      </Grid>
    </EditSaleForm>
  );
};

export default EditAirSaleForm;
